import { commonUtils, timeUtils } from '@sato/components';
import { differenceInMonths, differenceInYears, endOfDay, max } from 'date-fns';

import {
  IConditionalWrapper,
  IConditionalWrapperFields,
  IEntry,
  isContentType,
  ParsedEntry,
} from '@/contentful/types';
import { ContentMap } from '@/contentful/types/generated.contentMap';

import { APARTMENT_IS_CONSIDERED_NEW_YEARS } from '@constants/index';
import {
  createSaunasQueryKey,
  fetchApartmentSaunas,
} from '@hooks/useOmaSATOSaunas';
import {
  fetchSeparatelyInvoicedParkingSpaces,
  SEPARATELY_INVOICED_PARKING_SPACES_QUERY_KEY,
} from '@hooks/useParkingSpaceContracts';
import {
  ContractStatusCode,
  ParkingSpaceContract,
} from '@models/contract.types';
import { queryClient } from '@providers/QueryProvider';
import { ApartmentStore } from '@stores/apartmentStore';
import { ContractStore } from '@stores/contractStore';
import { UserStore } from '@stores/userStore';
import { isFeature } from '@utils/apartmentUtils';
import { isLocalOrTestEnv } from '@utils/featureFlags';
import { isClubHouseReservable } from '@views/ClubHouseReservationPage/clubHouse.utils';
import { isContractStatus } from '@views/ContractPage/ContractPage.utils';
import { isSATOParkingRentingParty } from '@views/parking/parking.utils';
import { hasOmaSATOReservableSaunas } from '@views/sauna/sauna.utils';

import {
  fetchWaterUsageRange,
  getWaterUsageRangeQueryKey,
} from '../Widgets/WaterUsage/WaterUsage.queries';

export type ConditionDependencies = {
  contractStore: ContractStore;
  apartmentStore: ApartmentStore;
  userStore: UserStore;
};

const asyncConditionKeys = [
  'hasWaterConsumptionData',
  'hasSaunaPrice',
  'hasActiveStatusParkingContract',
  'isUserNamedInParkingSpaceContract',
] satisfies Condition[];

type Condition = IConditionalWrapperFields['conditions'][number];

export type AsyncCondition = Extract<
  Condition,
  (typeof asyncConditionKeys)[number]
>;

export type SyncCondition = Exclude<Condition, AsyncCondition>;

const isActiveParkingSpaceContract = (contract: ParkingSpaceContract) =>
  contract.contractStatus === ContractStatusCode.ACTIVE_STATUS;

export const isAsyncCondition = (
  condition: Condition
): condition is AsyncCondition =>
  asyncConditionKeys.some((key) => key === condition);

export const isSyncCondition = (
  condition: Condition
): condition is SyncCondition => !isAsyncCondition(condition);

/** Resolve condition to a boolean value. Fallback to undefined, if an unknown condition is used */
const getCondition = (
  condition: SyncCondition,
  { contractStore, apartmentStore, userStore }: ConditionDependencies
): boolean | undefined => {
  switch (condition) {
    case 'isResident':
      return contractStore.isUserNonContractParty;
    case 'isContractParty':
      return contractStore.isUserOwnerOfAnyContract;
    case 'isContractStarting': {
      return isContractStatus(
        contractStore.selectedContract,
        ContractStatusCode.STARTING_STATUS
      );
    }
    case 'isEqualOrBeforeContractStartingDate': {
      const startDateISOString = contractStore.selectedContract?.startDate;
      if (!startDateISOString) return false;

      return timeUtils.isSameOrBefore(
        new Date(),
        endOfDay(new Date(startDateISOString))
      );
    }
    case 'isContractActive':
      return isContractStatus(
        contractStore.selectedContract,
        ContractStatusCode.ACTIVE_STATUS
      );
    case 'isContractResigned':
      return isContractStatus(
        contractStore.selectedContract,
        ContractStatusCode.RESIGNED_STATUS
      );
    case 'isContractEnded':
      return contractStore.isContractEnded;
    case 'isFullySATOProperty':
      return (
        apartmentStore.selectedApartment?.realestate.managementModel ===
        'FULL_SATO_TERRITORY'
      );
    case 'isExchangeCustomer':
      return userStore.user?.isExchangeCustomer;
    case 'isSmokeFreeApartment': {
      const apartment = apartmentStore.selectedApartment;

      if (!apartment) return false;

      return !!(
        apartment.apartment.anomalySmokeFree ?? apartment.realestate.smokeFree
      );
    }
    case 'hasReservableClubHouses': {
      const clubHouses =
        apartmentStore.selectedApartment?.realestate.realEstateFeatureLocations.filter(
          isFeature('CLUBHOUSE')
        );

      return !!clubHouses?.some(isClubHouseReservable);
    }
    case 'hasReservableSaunas': {
      return !!apartmentStore.selectedApartment?.realestate.realEstateFeatures.includes(
        'REAL_ESTATE_SAUNA'
      );
    }
    case 'hasGeneralMaintenanceAgreement': {
      return !!apartmentStore.selectedApartment?.realestate
        .maintenanceGeneralAgreement;
    }
    case 'hasBuildingBroadband': {
      const broadbandAgreementType =
        apartmentStore.selectedApartment?.apartment.internet?.agreement;
      return broadbandAgreementType === 'HOUSING_ASSOCATION';
    }
    case 'hasBuildingBroadbandIncludedInRent': {
      const broadbandAgreementType =
        apartmentStore.selectedApartment?.apartment.internet?.agreement;
      return broadbandAgreementType === 'SATO_FRAMEWORK';
    }
    case 'hasNoBuildingBroadband': {
      const broadbandAgreementType =
        apartmentStore.selectedApartment?.apartment.internet?.agreement;
      return !broadbandAgreementType || broadbandAgreementType === 'NONE';
    }
    case 'hasSafetyPlan': {
      return !!apartmentStore.selectedApartment?.realestate.pelSu;
    }
    case 'isEvicted': {
      return !!contractStore.selectedContract?.eviction;
    }
    case 'isSATOParkingRentingParty': {
      const realEstate = apartmentStore.selectedApartment?.realestate;
      const parkingAreas = realEstate?.parkingAreas;

      if (parkingAreas?.length) {
        return parkingAreas.some(isSATOParkingRentingParty);
      }

      return !!realEstate?.linkedParkingAreas?.some(isSATOParkingRentingParty);
    }
    case 'hasOmaSATOReservableSaunas': {
      if (!apartmentStore.selectedApartment) return false;

      return hasOmaSATOReservableSaunas(apartmentStore.selectedApartment);
    }
    case 'isApartmentNewlyRenovatedOrConstructed': {
      const apartment = apartmentStore.selectedApartment?.apartment;

      if (!apartment) return false;

      const renovationAndConstructionDates = [
        new Date(apartment.completitionDate),
        apartment.renovationDate && new Date(apartment.renovationDate),
      ].filter(commonUtils.isTruthy);

      const laterDate = max(renovationAndConstructionDates);

      return (
        differenceInYears(new Date(), laterDate) <
        APARTMENT_IS_CONSIDERED_NEW_YEARS
      );
    }
    // Whether contract is between 9 (inclusive) and 15 (exclusive) months old
    case 'isContract9to15MonthsOld': {
      const serializedContractStartDate =
        contractStore.selectedContract?.startDate;

      if (!serializedContractStartDate) return false;

      const monthDifference = differenceInMonths(
        new Date(),
        new Date(serializedContractStartDate)
      );

      return monthDifference >= 9 && monthDifference < 15;
    }
    // Whether contract is over 15 months old (inclusive)
    case 'isContractOver15MonthsOld': {
      const serializedContractStartDate =
        contractStore.selectedContract?.startDate;

      if (!serializedContractStartDate) return false;

      const contractStartDate = new Date(serializedContractStartDate);

      return differenceInMonths(new Date(), contractStartDate) >= 15;
    }
    case 'hasGsrnIdentifier':
      return !!apartmentStore.selectedApartment?.apartment.gsrnIdentifier;
    default:
      if (isLocalOrTestEnv) {
        console.error('Unrecognized ConditionalWrapper condition:', condition);
      }

      return undefined;
  }
};

/** Resolve an asyc condition to a boolean value. Fallback to undefined, if an unknown condition is used */
const getConditionAsync = async (
  condition: AsyncCondition,
  deps: ConditionDependencies
): Promise<boolean | undefined> => {
  const apartmentId = deps.apartmentStore.selectedApartmentID;

  switch (condition) {
    case 'hasWaterConsumptionData': {
      if (!apartmentId) return false;

      const result = await queryClient.fetchQuery(
        getWaterUsageRangeQueryKey(apartmentId),
        {
          queryFn: () => fetchWaterUsageRange(apartmentId),
        }
      );

      return !!result;
    }
    case 'hasSaunaPrice': {
      if (!apartmentId) return false;

      const result = await queryClient.fetchQuery(
        createSaunasQueryKey(apartmentId),
        () => fetchApartmentSaunas(apartmentId)
      );

      return commonUtils.nonNullable(result[0]?.price);
    }
    case 'hasActiveStatusParkingContract': {
      const isIncludedInRent =
        !!deps.apartmentStore.selectedApartment?.apartment.parkingSpaceId;
      const isInvoicedInContract =
        !!deps.contractStore.selectedContract?.parkingSpaces?.some(
          isActiveParkingSpaceContract
        );

      if (isIncludedInRent || isInvoicedInContract) return true;

      const separatelyInvoicedParkingSpaces = await queryClient.fetchQuery(
        SEPARATELY_INVOICED_PARKING_SPACES_QUERY_KEY,
        fetchSeparatelyInvoicedParkingSpaces
      );

      return separatelyInvoicedParkingSpaces.some(isActiveParkingSpaceContract);
    }
    case 'isUserNamedInParkingSpaceContract': {
      const crmId = deps.userStore.user?.crmID;

      if (!crmId) return false;
      if (deps.contractStore.isUserNamedInParkingSpaceContract(crmId)) {
        return true;
      }

      const separatelyInvoicedParkingSpaces = await queryClient.fetchQuery(
        SEPARATELY_INVOICED_PARKING_SPACES_QUERY_KEY,
        fetchSeparatelyInvoicedParkingSpaces
      );

      return separatelyInvoicedParkingSpaces.some((space) =>
        space.renterContactIds?.includes(crmId)
      );
    }
    default:
      if (isLocalOrTestEnv) {
        console.error(
          'Unrecognized ConditionalWrapper async condition:',
          condition
        );
      }

      return undefined;
  }
};

/** Check if all conditions defined in a Contentful ConditionalWrapper are met */
export const isValidConditions = (
  {
    conditions,
    negate,
  }: Pick<ParsedEntry<IConditionalWrapper>, 'negate'> & {
    conditions: SyncCondition[];
  },
  deps: ConditionDependencies
): boolean =>
  conditions.every((condition) => {
    const truthValue = getCondition(condition, deps);

    // ignore terms that could not be resolved to boolean values
    if (commonUtils.isNullable(truthValue)) return true;

    return negate ? !truthValue : truthValue;
  });

/** Check if all conditions defined in a Contentful ConditionalWrapper are met */
export const isValidConditionsAsync = async (
  {
    conditions,
    negate,
  }: Pick<ParsedEntry<IConditionalWrapper>, 'negate'> & {
    conditions: Condition[];
  },
  deps: ConditionDependencies
): Promise<boolean> => {
  for (const condition of conditions) {
    const truthValue = isAsyncCondition(condition)
      ? await getConditionAsync(condition, deps)
      : getCondition(condition, deps);

    if (negate ? truthValue === true : truthValue === false) {
      return false;
    }
  }

  return true;
};

/** Utility function for filtering entry lists that contain conditional wrappers.
 * Checks conditional wrapper conditions against application state and filters
 * only explicitly wanted content types */
// TODO: write unit tests
export const filterConditionalEntries = <
  T extends ParsedEntry<IEntry>,
  ContentT extends Exclude<T['contentType'], 'conditionalWrapper'>,
>(
  entries: T[],
  {
    deps,
    predicate,
  }: {
    /** state dependencies required for checking conditional wrapper conditions */
    deps: ConditionDependencies;
    /** additional predicate function for filtering entries after checking conditionality */
    predicate?: (entry: ContentMap[ContentT]) => boolean;
  }
): Array<ContentMap[ContentT]> => {
  // predicate function for filtering only desired content types and checking them agains an additional predicate param function
  const isValidEntry = (
    entry: ParsedEntry<IEntry>
  ): entry is ContentMap[ContentT] => {
    return (
      // check additional predicate function
      !predicate || predicate(entry as ContentMap[ContentT])
    );
  };

  return entries.reduce<Array<ContentMap[ContentT]>>((acc, entry) => {
    if (isContentType(entry, 'conditionalWrapper')) {
      const syncConditions = entry.conditions.filter(isSyncCondition);
      const conditionalEntries =
        (isValidConditions({ ...entry, conditions: syncConditions }, deps)
          ? filterConditionalEntries(entry.content, { deps })
          : entry.fallbackContent) ?? [];

      return [
        ...acc,
        // for some reason TS doesn't infer entry type correctly regardless of the isValidEntry type-guard
        ...(conditionalEntries.filter(isValidEntry) as Array<
          ContentMap[ContentT]
        >),
      ];
    } else if (isValidEntry(entry)) {
      return [...acc, entry];
    }

    return acc;
  }, []);
};
